import { useState, useEffect, useCallback, useRef } from 'react';
import { getExhibitionVowList } from '../api/profileService';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { CARD_STYLES } from '@features/wish-tree/constants';

// 至心發願紀錄
function WishTreeRecordCard({ item }) {
  const dateObj = new Date(item.created_at);
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  const dateStr = `${m}/${d}`;
  const cardImage =
    CARD_STYLES.find((c) => c.id === item.image_id)?.image_s || CARD_STYLES[0].image_s;

  return (
    <div className="self-stretch px-2.5 py-2 bg-gradient-to-br from-orange-100/80 via-white to-orange-100/60 rounded-lg outline outline-1 outline-offset-[-1px] outline-[#6E625B]/10 flex flex-row justify-start items-center gap-4">
      <div className="w-12 h-12 relative overflow-hidden flex-shrink-0 bg-white rounded flex justify-center items-center shadow-sm">
        <img className="w-10 h-10 object-contain" src={cardImage} alt="wish card" />
      </div>
      <div className="flex-1 grid grid-cols-1 gap-1 items-center w-full min-w-0">
        <div className="text-[#C59B5A] text-sm font-bold font-['Noto_Sans_TC'] leading-5 text-left truncate">
          {item.vower_name}
        </div>
        <div className="text-[#6E625B] text-xs font-['Noto_Sans_TC'] leading-4 text-left truncate">
          {item.content}
        </div>
      </div>
      <div className="flex flex-col items-end justify-center flex-shrink-0 w-16">
        <div className="text-[#C59B5A] text-sm font-semibold font-['Noto_Sans_TC'] leading-5 whitespace-nowrap">
          {item.point > 0 ? `+${item.point}` : ''}
        </div>
        <div className="text-[#6E625B] text-xs font-['Noto_Sans_TC'] leading-4 whitespace-nowrap">
          {dateStr}
        </div>
      </div>
    </div>
  );
}

function WishTreeList({ isOpen, onClose }) {
  const { t } = useLanguage();

  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const listRef = useRef(null);

  const PER_PAGE = 10;

  const fetchPage = useCallback(
    async (targetPage) => {
      setLoading(true);

      try {
        const payload = {
          page: targetPage,
          per_page: PER_PAGE,
        };

        const res = await getExhibitionVowList(payload);
        const newItems = res?.items ?? [];
        const totalCount = res?.total_items ?? newItems.length;

        console.log(
          'WishTreeList API items order:',
          newItems.map((i) => ({ record_id: i.record_id, content: i.content, created_at: i.created_at }))
        );

        setItems((prev) => (targetPage === 1 ? newItems : [...prev, ...newItems]));
        setTotal(totalCount);
        setTotalPoints(res?.total_points ?? 0);
        setHasMore(targetPage * PER_PAGE < totalCount);
        setPage(targetPage);
      } catch (err) {
        console.error('WishTreeList fetch error:', err);
        if (targetPage === 1) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setItems([]);
    setHasMore(false);
    fetchPage(1);
  }, [isOpen, fetchPage]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10;
    if (bottom && !loading && hasMore) {
      fetchPage(page + 1);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdropClick}
    >
      <div
        className="
        relative bg-white rounded-[20px] flex flex-col gap-4 overflow-hidden
        w-full max-w-sm
        h-[90vh] max-h-[785px]
        p-6
        shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10),0px_42px_42px_0px_rgba(0,0,0,0.09),0px_95px_57px_0px_rgba(0,0,0,0.05)]
      "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between flex-shrink-0">
          <div className="text-[#171717]/95 text-xl sm:text-2xl font-bold font-['Noto_Serif_TC'] leading-7">
            {t('wish-tree-list.header')}
          </div>
          <button
            onClick={onClose}
            aria-label={t('wish-tree-list.closeAria')}
            className="w-6 h-6 flex items-center justify-center flex-shrink-0 hover:opacity-60 transition-opacity"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9.25" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" />
              <path d="M7 7L13 13M13 7L7 13" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex justify-between items-center bg-[#FDFBF9] py-2 px-3 rounded-lg text-[#6E625B] border border-[#6E625B]/10">
          <div className="text-sm">{t('wish-tree-list.totalRecords').replace('{{count}}', total)}</div>
          <div className="text-sm font-bold text-[#BC313D]">{t('wish-tree-list.totalPoints').replace('{{points}}', totalPoints)}</div>
        </div>

        <div ref={listRef} onScroll={handleScroll} className="flex-1 overflow-y-auto -mx-1 px-1">
          {items.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" opacity="0.3">
                <circle cx="18" cy="18" r="11.5" stroke="#6E625B" strokeWidth="2" />
                <path d="M27 27L35 35" stroke="#6E625B" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p className="text-[#6E625B]/40 text-sm font-['Noto_Sans_TC']">
                {t('wish-tree-list.noRecords')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((item, idx) => (
                <WishTreeRecordCard key={item.wish_id ?? item.record_id ?? idx} item={item} />
              ))}
              {loading && (
                <div className="flex flex-col gap-2">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="h-[68px] rounded-lg bg-orange-50/60 animate-pulse"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WishTreeList;
